import React from 'react';
import DividerHeader from '../divider-header';
import Container from '../container';
import SectionHeader from '../section-header';
import FancyLink from '../fancy-link';
import styled from 'styled-components';
import * as vars from '../../styles/vars';
import _ from 'lodash';
import data from './data';

const frontEndWork = _.filter(data, item => item.section === 'frontEnd');
const fullStackWork = _.filter(data, item => item.section === 'fullStack');

const AgencyAttribution = ({ item }) => {
  if (!item.workingFor) return null;
  return (
    <ProjectMeta>
      with{' '}
      {item.workingForLink ? (
        <a href={item.workingForLink} target="_blank" rel="noopener noreferrer">
          {item.workingFor}
        </a>
      ) : (
        item.workingFor
      )}
    </ProjectMeta>
  );
};

const ProjectDetails = ({ item }) => {
  if (!item.summary && !item.details) return null;

  return (
    <ProjectDetailsWrapper>
      {item.summary && <p>{item.summary}</p>}
      {item.details && item.details.length > 0 && (
        <ul>
          {_.map(item.details, detail => (
            <li key={_.kebabCase(detail)} dangerouslySetInnerHTML={{ __html: detail }} />
          ))}
        </ul>
      )}
    </ProjectDetailsWrapper>
  );
};

const About = () => {
  return (
    <>
      <Container id="work">
        <DividerHeader title="Work" data-anim-in />
      </Container>
      <SectionHeader slim lessTopPadding lessBottomPadding>
        <div data-anim-in>
          <p>
            {' '}
            Here is a tiny sample of recent-ish <em>front-end</em> work. Some
            of the more interesting product and platform work sits behind
            logins, so I&rsquo;ve added a little context where a public link
            does not tell the whole story. I&rsquo;m happy to discuss that side
            of things in detail over a coffee and/or a pastry.
          </p>
        </div>
      </SectionHeader>

      <Container slim>
        <ProjectList>
          {_.map(frontEndWork, (item, index) => (
            <ProjectListItem key={_.kebabCase(`${item.title}-${index}`)} data-anim-in>
              <FancyLink
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                distanceThreshold={item.distanceThreshold || { min: 0, max: 200 }}
                data-hover
              >
                {item.title}
              </FancyLink>
             {item.workingFor && <AgencyAttribution item={item} />}
              {item.tags && item.tags.length > 0 && (
                <RoleTags>{item.tags.join(' · ')}</RoleTags>
              )}
              <ProjectDetails item={item} />
            </ProjectListItem>
          ))}
        </ProjectList>

        <SubHeading data-anim-in as="h3">
          Design, front-end and back-end:
        </SubHeading>

        <ProjectList>
          {_.map(fullStackWork, (item, index) => (
            <ProjectListItem key={_.kebabCase(`${item.title}-${index}`)} data-anim-in>
              <FancyLink
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                distanceThreshold={item.distanceThreshold || { min: 0, max: 200 }}
                data-hover
              >
                {item.title}
              </FancyLink>
              {item.workingFor && <AgencyAttribution item={item} />}
              {item.tags && item.tags.length > 0 && (
                <RoleTags>{item.tags.join(' · ')}</RoleTags>
              )}
              <ProjectDetails item={item} />
            </ProjectListItem>
          ))}
        </ProjectList>
      </Container>
    </>
  );
};

const ProjectList = styled.ul`
  color: ${vars.colors.brand};
  list-style: none;
  margin: 0 0 120px;

  p {
    color: ${vars.colors.monsoon};
    margin-top: 1em;
    font-size: 22px;
  }
`;

const ProjectListItem = styled.li`
  margin-bottom: 0.8em;

  ${vars.browserHack.firefox`
    margin-bottom: 1.1em;
  `}

  ${vars.browserHack.edge`
    margin-bottom: 1.1em;
  `}

  ${vars.breakpoint.lg`
    margin-bottom: .2em;

    ${vars.browserHack.firefox`
    margin-bottom: 1.5em;
  `}

  ${vars.browserHack.edge`
    margin-bottom: 1.1em;
  `}
  `}

  > a {
    font-size: 38px;
    color: ${vars.colors.black};
    border-bottom: 4px solid #f3f3f3;
    text-shadow: -2px -2px #fff, -2px 2px #fff, 2px -2px #fff, 2px 2px #fff;
    line-height: 1.4;
    display: inline;


    ${vars.browserHack.firefox`
    line-height: 1.1;
    `}

    ${vars.browserHack.edge`
    line-height: 1.1;
    `}

    ${vars.breakpoint.md`
      display: inline-block;
    `}

    ${vars.breakpoint.lg`
      font-size: 50px;
    `}

    &:hover {
      color: ${vars.colors.brand};

      span {
        background-color: ${vars.colors.brand};
      }
    }

    span {
      transition: background-color 0.3s ease-in-out;
      height: 4px;
      bottom: -4px;
      background-color: ${vars.colors.greyLighter};
    }
  }
`;

const ProjectMeta = styled.div`
  margin-top: 0.35em;
  font-size: 17px;
  line-height: 1.45;
  color: ${vars.colors.monsoon};

  a {
    font-size: inherit;
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    text-shadow: none;
    line-height: inherit;
    display: inline;
    transition: color 0.2s ease, border-color 0.2s ease;

    &:hover {
      color: ${vars.colors.grey};
      border-bottom-color: ${vars.colors.iron};
    }
  }
`;

const RoleTags = styled.div`
  margin-top: 0.28em;
  font-size: 15px;
  line-height: 1.4;
  color: ${vars.colors.monsoon};
  letter-spacing: 0.02em;
`;

const ProjectDetailsWrapper = styled.div`
  color: ${vars.colors.monsoon};
  font-size: 18px;
  line-height: 1.5;
  margin-top: 1em;
  max-width: 780px;

  p {
    margin: 0 0 0.8em;
    font-size: 20px;
  }

  ul {
    list-style: none;
    margin: 0 0 1em 0;

    ${vars.breakpoint.lg`
      margin: 0 0 1.2em 0.06em;
    `}
  }

  li {
    margin-bottom: 0.45em;
    padding-left: 1em;
    position: relative;

    &:before {
      background-color: ${vars.colors.brand};
      border-radius: 50%;
      content: '';
      height: 0.3em;
      left: 0;
      position: absolute;
      top: 0.6em;
      width: 0.3em;
    }

    a {
      color: ${vars.colors.grey};
      text-decoration: underline;
      text-shadow: none;
      line-height: inherit;
      display: inline;
      transition: color 0.2s ease;

      &:hover {
        color: ${vars.colors.black};
      }
    }

  }
`;

const SubHeading = styled.h4`
  font-size: 28px;

  ${vars.breakpoint.lg`
    font-size: 40px;
  `}
`;

export default About;
